import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import { useOrgStore } from '@/stores/orgStore';
import type { AuditLogEntry, Json, OrgRole } from '@/types/database.types';

type AuditLogProfile = {
  user_id: string;
  full_name: string | null;
  avatar_url: string | null;
};

type AuditLogMember = {
  user_id: string;
  role: OrgRole;
  employee_code: string | null;
};

type AuditLogActor = {
  user_id: string;
  full_name: string | null;
  avatar_url: string | null;
  role: OrgRole | null;
  employee_code: string | null;
};

export type AuditLogEntryWithProfile = AuditLogEntry & {
  profiles?: {
    full_name: string | null;
    avatar_url: string | null;
  } | null;
  actor?: AuditLogActor | null;
  subject?: AuditLogActor | null;
};

function getTargetUserId(details: Json | null): string | null {
  if (!details || typeof details !== 'object' || Array.isArray(details)) return null;
  const targetUserId = (details as Record<string, Json | undefined>).target_user_id;
  return typeof targetUserId === 'string' && targetUserId ? targetUserId : null;
}

export function useAuditLog(filters?: { userId?: string; action?: string; limit?: number }) {
  const orgId = useOrgStore((s) => s.organization?.id);

  return useQuery({
    queryKey: ['audit-log', orgId, filters],
    enabled: !!orgId,
    queryFn: async () => {
      let query = supabase
        .from('audit_logs')
        .select('*')
        .eq('organization_id', orgId!)
        .order('created_at', { ascending: false });

      if (filters?.userId) {
        query = query.eq('user_id', filters.userId);
      }
      if (filters?.action) {
        query = query.eq('action', filters.action);
      }
      if (filters?.limit) {
        query = query.limit(filters.limit);
      }

      const { data, error } = await query;
      if (error) throw error;

      const logs = data ?? [];
      const userIds = Array.from(new Set(
        logs.flatMap((entry) => [entry.user_id, getTargetUserId(entry.details)]).filter(Boolean),
      )) as string[];

      if (userIds.length === 0) {
        return logs as AuditLogEntryWithProfile[];
      }

      const [profilesResult, membersResult] = await Promise.all([
        supabase
          .from('profiles')
          .select('user_id, full_name, avatar_url')
          .in('user_id', userIds),
        supabase
          .from('org_members')
          .select('user_id, role, employee_code')
          .eq('organization_id', orgId!)
          .eq('is_active', true)
          .in('user_id', userIds),
      ]);

      if (profilesResult.error) throw profilesResult.error;
      if (membersResult.error) throw membersResult.error;

      const profilesByUserId = new Map(
        ((profilesResult.data ?? []) as AuditLogProfile[]).map((profile) => [
          profile.user_id,
          {
            full_name: profile.full_name,
            avatar_url: profile.avatar_url,
          },
        ]),
      );

      const membersByUserId = new Map(
        ((membersResult.data ?? []) as AuditLogMember[]).map((member) => [
          member.user_id,
          member,
        ]),
      );

      return logs.map((entry) => ({
        ...entry,
        profiles: entry.user_id ? profilesByUserId.get(entry.user_id) ?? null : null,
        actor: entry.user_id
          ? (() => {
              const profile = profilesByUserId.get(entry.user_id);
              const member = membersByUserId.get(entry.user_id);
              return {
                user_id: entry.user_id,
                full_name: profile?.full_name ?? null,
                avatar_url: profile?.avatar_url ?? null,
                role: member?.role ?? null,
                employee_code: member?.employee_code ?? null,
              };
            })()
          : null,
        subject: (() => {
          const subjectUserId = getTargetUserId(entry.details);
          if (!subjectUserId) return null;
          const profile = profilesByUserId.get(subjectUserId);
          const member = membersByUserId.get(subjectUserId);
          return {
            user_id: subjectUserId,
            full_name: profile?.full_name ?? null,
            avatar_url: profile?.avatar_url ?? null,
            role: member?.role ?? null,
            employee_code: member?.employee_code ?? null,
          };
        })(),
      })) as AuditLogEntryWithProfile[];
    },
  });
}
